import wNumb from "wnumb";

import { ContentPanel, ContentPanelInteraction, EtfLabel, LinkButton } from 'shared/ui-kit';

import { FactorViewModel } from '../model';

import styles from "./factor.module.scss";
import { PropsWithClassName } from "shared/props";
import classNames from "classnames";

import { createHelpHref } from '../utils';
import { UserClaims } from "shared/mechanics/auth";

const correlationFormat = wNumb({ decimals: 0, suffix: '%' }).to;
const digit2Format = wNumb({ decimals: 2, suffix: '' }).to;
const probabilityFormat = wNumb({ decimals: 0, suffix: '%', }).to;

export interface EtfFactorAnalysisFactorProps {
	ticker: string;
	factor: FactorViewModel;
	isExpandedByDefault: boolean;
}
export const EtfFactorAnalysisFactor = (props: EtfFactorAnalysisFactorProps & PropsWithClassName) => {
	const dependenceIconClass = calculateDependenceIconClass();

	const dependenceIcon = <div className={classNames(styles.dependenceIcon, dependenceIconClass)}></div>;

	return (
		<ContentPanel 
			anchor={props.factor.ticker}
			title={<EtfLabel ticker={props.ticker}
			suffix={<>и {props.factor.description} {dependenceIcon} </>} />}
			className={props.className}
			defaults={{ isExpanded: props.isExpandedByDefault }}
			help={{ href: createHelpHref("factor") }}
			expandPermissions={{ claim: UserClaims.HAVE_SUBSCRIPTION, title: "Больше риск менеджмента в подписке" }}>
			<ContentPanelInteraction className={styles.interaction}>
				<p>
					<LinkButton
						title="СРАВНИТЬ ГРАФИКИ"
						href={`/etf/report/${props.ticker}?options=vs=${props.factor.ticker}`}
						className={styles.button}
						rel="nofollow"
						target="_blank"
						iconClass="fa fa-chart-line" /> vs {props.factor.ticker}
				</p>
			</ContentPanelInteraction>
			<div className={styles.container}>
				<div className={styles.item}>
					<Dependence ticker={props.ticker} factor={props.factor} />
				</div>
				<div className={styles.item}>
					<Details ticker={props.ticker} factor={props.factor} />
				</div>
			</div>
		</ContentPanel>
	);

	function calculateDependenceIconClass() {
		const correlation = Math.abs(props.factor.correlation);
		if (correlation > 0.7)
			return styles.high;
		if (correlation > 0.3)
			return styles.average;
		return styles.low;
	}
}

const Dependence = (props: { ticker: string, factor: FactorViewModel }) => {
	const [yearDependenceSymbol, yearDependenceClass] = props.factor.overYield1Y || 0 > 0
		? ['выше', styles.positive]
		: ['ниже', styles.negative];
	const [dependenceSymbol, dependenceClass] = dependenceParams();
	const yearDependenceValue = correlationFormat(Math.abs(props.factor.overYield1Y || 0) * 100);
	const beta = digit2Format(props.factor.beta);
	const correlation = digit2Format(props.factor.correlation);

	return (
		<div className={styles.dependence}>
			{
				props.factor.overYield1Y === null
					? null
					: (
						<p className={styles.first_row}>
							Годовая доходность {props.ticker} <span className={yearDependenceClass}>{yearDependenceSymbol}</span> на <span className={yearDependenceClass}>{yearDependenceValue}</span>, чем {props.factor.description}.
						</p>
					)
			}
			<p className={styles.second_row}>
				<span>Зависимость: <span className={dependenceClass}>{dependenceSymbol}</span></span>
				<span>Корреляция: {correlation}</span>
				<span>Бета: {beta}</span>
			</p>
		</div>
	);

	function dependenceParams() {
		const correlation = Math.abs(props.factor.correlation);
		if (correlation > 0.7)
			return ['высокий', styles.high];
		if (correlation > 0.3)
			return ['средний', styles.average];
		return ['низкий', styles.low];
	}
}

const Details = (props: { ticker: string; factor: FactorViewModel; }) => {
	return (
		<div className={classNames("flex-container-horizontal", "flex-container-horizontal-break-1000")}>
			<div className={classNames("flex-size-fill", styles.details_delimeter)}>
				<p className={styles.details}>
					{props.ticker} и {props.factor.description} <span className={styles.negative}>падали</span> одновременно {props.factor.down.otherDownCount} раз(а) / {probabilityFormat((props.factor.down.otherDownCount / props.factor.down.count) * 100)} вероятности
				</p>
				<p className={styles.details}>
					{props.ticker} <span className={styles.negative}>падал</span>, а у {props.factor.description} был <span className={styles.positive}>рост</span> {props.factor.down.otherUpCount} раз(а) / {probabilityFormat((props.factor.down.otherUpCount / props.factor.down.count) * 100)} вероятности
				</p>
			</div>
			<div className="flex-size-fill">
				<p className={styles.details}>
					{props.ticker} <span className={styles.positive}>рос</span>, а у {props.factor.description} было <span className={styles.negative}>падение</span> {props.factor.up.otherDownCount} раз(а) / {probabilityFormat((props.factor.up.otherDownCount / props.factor.up.count) * 100)} вероятности
				</p>
				<p className={styles.details}>
					{props.ticker} и {props.factor.description} <span className={styles.positive}>росли</span> одновременно {props.factor.up.otherUpCount} раз(а) / {probabilityFormat((props.factor.up.otherUpCount / props.factor.up.count) * 100)} вероятности
				</p>
			</div>
		</div>
	)
}